import { ColumnDef, getCoreRowModel, useReactTable, getExpandedRowModel, ExpandedState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { ObjectEntityFragment } from 'pages/facilities/api/fragments/generated/ObjectEntity.fragment';
import Across from 'assets/icons/Across';
import Check from 'assets/icons/check';
import { Link } from 'react-router-dom';
import { getForemen, getSupervisors } from 'pages/facilities/lib/helpers/responsibleUsersHelper';
import Depth from 'assets/icons/Depth';
import ParentObject from 'assets/icons/ParentObject';
import ChildObject from 'assets/icons/ChildObject';

export const useFacilities2Table = (data: ObjectEntityFragment[]) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const columns: ColumnDef<ObjectEntityFragment>[] = useMemo(() => {
    return [
      {
        header: 'Объекты',
        size: 300,
        cell: ({ row: { original, depth } }) => {
          const getPadding = (depth: number) => {
            if (depth === 1) return 8;
            if (depth === 2) return 34;
            return 0;
          };

          const children = original.children;

          const hasChildren = children && children.length > 0;

          return (
            <div className="flex items-center gap-2" style={{ paddingLeft: `${getPadding(depth)}px` }}>
              {depth > 0 && (
                <div className="relative -top-[4px]">
                  <Depth />
                </div>
              )}
              {hasChildren ? <ParentObject /> : <ChildObject />}
              <Link className="link" to={`/facilities/${original.id}`} onClick={e => e.stopPropagation()}>
                {original.name}
                {hasChildren ? ` (${children?.length})` : ''}
              </Link>
            </div>
          );
        },
      },

      {
        header: 'Город',
        size: 200,
        cell: ({ row: { original } }) => original.settings?.city?.name || '—',
      },
      {
        header: 'Супервайзеры',
        size: 270,
        cell: ({ row: { original } }) => {
          const supervisors = getSupervisors(original);
          if (supervisors.length === 0) return '—';

          return (
            <div className="flex flex-col gap-1">
              {supervisors.map(supervisor => (
                <Link key={supervisor.id} className="text-sm link" to={`/users/${supervisor.id}`}>
                  {supervisor.name}
                </Link>
              ))}
            </div>
          );
        },
      },
      {
        header: 'Бригадиры',
        size: 270,
        cell: ({ row: { original } }) => {
          const foremen = getForemen(original);
          if (foremen.length === 0) return '—';

          return (
            <div className="flex flex-col gap-1">
              {foremen.map(foreman => (
                <Link key={foreman.id} className="text-sm link" to={`/users/${foreman.id}`}>
                  {foreman.name}
                </Link>
              ))}
            </div>
          );
        },
      },
      {
        header: 'Адрес',
        size: 280,
        cell: ({ row: { original } }) => original.settings?.address?.addressText || '—',
      },
      {
        header: 'HRMS ID',
        size: 150,
        cell: ({ row: { original } }) => original.hrmsId || '—',
      },
      {
        header: 'Юр. лицо в наймикс',
        size: 250,
        cell: ({ row: { original } }) => original.settings?.naimixLegalEntity?.name || '—',
      },
      {
        header: 'Статус',
        size: 80,
        cell: ({ row: { original } }) => (
          <div className="flex items-center justify-center">
            {original.isActive ? <Check className="text-smena_green-dark" /> : <Across className="text-smena_red" />}
          </div>
        ),
      },
    ];
  }, []);

  const [columnOrder, setColumnOrder] = useState(columns.map(column => column.header as string));

  return useReactTable({
    data,
    state: {
      columnOrder,
      expanded,
    },
    getSubRows: row => row.children?.map(child => child) || [],
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnOrderChange: setColumnOrder,
    onExpandedChange: setExpanded,
    columnResizeMode: 'onChange',
    columns,
  });
};
